import React, {useCallback, useEffect, useState} from 'react';
import Popper, {PopperProps} from '@material-ui/core/Popper';
import Fade from '@material-ui/core/Fade';

interface InteractivePopperProps extends Partial<PopperProps> {
  anchorEl: HTMLDivElement | null;
}

type Timer = ReturnType<typeof setTimeout>;
const CLOSE_TIMER = 300;

/**
 * A mui Popper which stays open as long as the popover or element has mouseover
 */
export const InteractivePopper: React.FC<InteractivePopperProps> = ({
  children,
  anchorEl,
  open,
  ...rest
}) => {
  const [hasMouseover, setHasMouseover] = useState(false);
  const [closeTimer, setCloseTimer] = useState<Timer | null>(null);

  const onMouseEnter = useCallback(() => {
    if (closeTimer) {
      clearTimeout(closeTimer);
      setCloseTimer(null);
    }
    setHasMouseover(true);
  }, [closeTimer, setCloseTimer, setHasMouseover]);
  const onMouseLeave = useCallback(() => {
    setCloseTimer(setTimeout(() => setHasMouseover(false), CLOSE_TIMER));
  }, [setCloseTimer, setHasMouseover]);

  useEffect(() => {
    if (anchorEl) {
      anchorEl.addEventListener('mousemove', onMouseEnter);
      anchorEl.addEventListener('mouseenter', onMouseEnter);
      anchorEl.addEventListener('mouseleave', onMouseLeave);

      return () => {
        anchorEl.removeEventListener('mousemove', onMouseEnter);
        anchorEl.removeEventListener('mouseenter', onMouseEnter);
        anchorEl.removeEventListener('mouseleave', onMouseLeave);
      };
    }
  }, [anchorEl, onMouseEnter, onMouseLeave]);

  return (
    <Popper open={!!anchorEl && (open || hasMouseover)} transition anchorEl={anchorEl} {...rest}>
      {({TransitionProps}) => (
        <Fade {...TransitionProps} timeout={200}>
          <div
            className="interactive-popper-content"
            onMouseEnter={onMouseEnter}
            onMouseMove={onMouseEnter}
            onMouseLeave={onMouseLeave}>
            {children}
          </div>
        </Fade>
      )}
    </Popper>
  );
};
